import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Users, MessageSquare, CheckCircle2, Star, Loader2, Trophy } from 'lucide-react';
import { reportsAPI } from '../../services/api';

interface AgentPerformanceProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

interface AgentData {
  agent_id: number;
  agent_name: string;
  conversations_handled: number;
  messages_sent: number;
  avg_response_time: number;
  resolution_count: number;
  csat_score: number;
}

const getPerformanceVariant = (score: number): 'secondary' | 'outline' | 'destructive' => {
  if (score > 80) return 'secondary';
  if (score > 60) return 'outline';
  return 'destructive';
};

const getPerformanceLabel = (score: number): string => {
  if (score > 80) return 'Excellent';
  if (score > 60) return 'Good';
  return 'Needs Improvement';
};

export const AgentPerformance: React.FC<AgentPerformanceProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentData, setAgentData] = useState<AgentData[]>([]);

  useEffect(() => {
    fetchAgentPerformance();
  }, [dateRange]);

  const fetchAgentPerformance = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      const data = await reportsAPI.getAgentReport(params);
      setAgentData(data.agents || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch agent performance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!agentData.length) {
    return (
      <Alert>
        <AlertDescription>No agent performance data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  const totalConversations = agentData.reduce((sum, agent) => sum + agent.conversations_handled, 0);
  const totalMessages = agentData.reduce((sum, agent) => sum + agent.messages_sent, 0);
  const totalResolutions = agentData.reduce((sum, agent) => sum + agent.resolution_count, 0);
  const avgCSAT = agentData.reduce((sum, agent) => sum + agent.csat_score, 0) / agentData.length;

  const sortedAgents = [...agentData].sort((a, b) => b.conversations_handled - a.conversations_handled);
  const topPerformer = sortedAgents[0];

  const exportData = agentData || [];

  return (
    <div className="space-y-6">

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{agentData.length}</div>
            <p className="text-xs text-muted-foreground">Active in period</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Conversations</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalConversations.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">{totalMessages.toLocaleString()} messages sent</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Resolutions</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalResolutions.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Across all agents</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg CSAT Score</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgCSAT.toFixed(1)}/5.0</div>
            <p className="text-xs text-muted-foreground">Customer satisfaction</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performer Highlight */}
      {topPerformer && (
        <Card className="bg-muted/30">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Top Performer: {topPerformer.agent_name}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-4">
              <div>
                <p className="text-xs text-muted-foreground">Conversations Handled</p>
                <p className="text-2xl font-bold">{topPerformer.conversations_handled}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Messages Sent</p>
                <p className="text-2xl font-bold">{topPerformer.messages_sent}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Resolutions</p>
                <p className="text-2xl font-bold">{topPerformer.resolution_count}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">CSAT Score</p>
                <p className="text-2xl font-bold">{topPerformer.csat_score.toFixed(1)}/5</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Agent Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Individual Agent Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead className="text-right">Conversations</TableHead>
                <TableHead className="text-right">Messages Sent</TableHead>
                <TableHead className="text-right">Avg Response Time</TableHead>
                <TableHead className="text-right">Resolutions</TableHead>
                <TableHead className="text-right">Resolution Rate</TableHead>
                <TableHead className="text-right">CSAT Score</TableHead>
                <TableHead className="text-right">Performance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedAgents.map((agent, index) => {
                const resolutionRate = agent.conversations_handled > 0
                  ? (agent.resolution_count / agent.conversations_handled) * 100
                  : 0;

                const isTopPerformer = index === 0;
                const performanceScore = (agent.conversations_handled * 0.4) +
                  (agent.resolution_count * 0.4) +
                  (agent.csat_score * 20 * 0.2);

                return (
                  <TableRow key={agent.agent_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs">
                            {agent.agent_name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium">{agent.agent_name}</span>
                        {isTopPerformer && (
                          <Badge variant="secondary" className="ml-1">Top</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {agent.conversations_handled}
                    </TableCell>
                    <TableCell className="text-right">{agent.messages_sent}</TableCell>
                    <TableCell className="text-right">
                      {agent.avg_response_time > 0 ? `${agent.avg_response_time} min` : 'N/A'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {agent.resolution_count}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Progress value={Math.min(resolutionRate, 100)} className="w-16 h-1.5" />
                        <span className="text-sm w-10">{resolutionRate.toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Star className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{agent.csat_score.toFixed(1)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={getPerformanceVariant(performanceScore)}>
                        {getPerformanceLabel(performanceScore)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
